/**
 * The one typed reader of `customDetails.intake_snapshot` (SA-461, sweep
 * spec §7.1, §9): the built-in register, the Compass pricing view, the
 * Contact card and the route cells all read this shape and cast it inline
 * four times before this file. A slot reads the way every one of them read
 * it — a string trimmed (empty is absent), a number as its digits, a place
 * (SA-1051) as its words, anything else absent.
 */
import type { Order } from '@/types/commerce'
import { isPlace, placeWords } from './place'
import { isWindow, windowWords } from './window'

export interface IntakeSnapshot {
  slots: Record<string, unknown>
  sources?: unknown[]
  provenance?: Record<string, unknown>
  /** SA-476 (spec D1): whether the birth carried a source (`sources[0]`); absent on a conversation birth or a record born before SA-476. */
  bornWithSource?: boolean
}

/** The shipment-quote base's slot ids the core names (the register and the
 * strip read them; the pack owns only their prompt hints, SA-450). */
export const INTAKE_SLOT = {
  origin: 'origin',
  destination: 'destination',
  cargoDescription: 'cargo_description',
  cargoSize: 'cargo_size',
  neededWindow: 'needed_window',
  company: 'requesting_company',
  contactName: 'contact_name',
  quoteDeadline: 'quote_deadline',
  email: 'requester_email',
  phone: 'customer_phone',
  customerType: 'customer_type',
} as const

type Source = Pick<Order, 'customDetails'>

const isObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v)

export function intakeSnapshot(order: Source): IntakeSnapshot | null {
  const raw = order.customDetails?.intake_snapshot
  if (!isObject(raw)) return null
  return {
    slots: isObject(raw.slots) ? raw.slots : {},
    ...(Array.isArray(raw.sources) ? { sources: raw.sources } : {}),
    ...(isObject(raw.provenance) ? { provenance: raw.provenance } : {}),
    ...(typeof raw.born_with_source === 'boolean' ? { bornWithSource: raw.born_with_source } : {}),
  }
}

/** The raw slot value: a string, a number, a place (SA-1051), whatever is stored. */
export function intakeValue(order: Source, key: string): unknown {
  return intakeSnapshot(order)?.slots[key]
}

export function intakeSlot(order: Source, key: string): string | null {
  const value = intakeValue(order, key)
  if (isWindow(value)) return windowWords(value) || null  // SA-1136: a window reads its words
  if (isPlace(value)) return placeWords(value) || null    // SA-1051: a place reads its words
  if (typeof value === 'string') return value.trim() || null
  if (typeof value === 'number') return String(value)
  return null
}

/** "HKG → LAX" — the words the server's route fallback produces (SA-453 D6). */
export function intakePair(order: Source): string | null {
  const origin = intakeSlot(order, INTAKE_SLOT.origin)
  const destination = intakeSlot(order, INTAKE_SLOT.destination)
  return origin && destination ? `${origin} → ${destination}` : null
}

/** SA-1456 spec §7.5: the intake slots a person can link to a company, in the order they show. */
export const INTAKE_LINK_SLOTS = ['requesting_company', 'contractual_partner'] as const
export type IntakeLinkSlot = (typeof INTAKE_LINK_SLOTS)[number]

export const isIntakeLinkSlot = (id: string): id is IntakeLinkSlot =>
  (INTAKE_LINK_SLOTS as readonly string[]).includes(id)

/**
 * The company ids stored on the snapshot, by slot (`intake_snapshot.links.<slot>.company_id`), as
 * picked. This is what was linked, not what the link reads as now: the intake read serves the
 * company a merge leads to, and only while the slot's text still answers to it.
 */
export function intakeLinks(order: Source): Partial<Record<IntakeLinkSlot, string>> {
  const raw = order.customDetails?.intake_snapshot
  const links = isObject(raw) && isObject(raw.links) ? raw.links : {}
  const out: Partial<Record<IntakeLinkSlot, string>> = {}
  for (const slot of INTAKE_LINK_SLOTS) {
    const entry = links[slot]
    const id = isObject(entry) ? entry.company_id : undefined
    if (typeof id === 'string' && id) out[slot] = id
  }
  return out
}
